import { Button, Card, Col, Popconfirm, Row, Statistic, Table, Tag, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTrading } from '../context/TradingContext';
import { STARTING_CASH } from '../config';

const { Title, Text } = Typography;

export default function Portfolio() {
  const { portfolio, quotes, reset, totalValue, totalPnl, totalPnlPct } = useTrading();

  const posRows = portfolio.positions.map(pos => {
    const q = quotes.find(q => q.symbol === pos.symbol);
    const currentPrice = q?.price ?? pos.avgCost;
    const marketValue = currentPrice * pos.shares;
    const costBasis = pos.avgCost * pos.shares;
    const pnl = marketValue - costBasis;
    const pnlPct = (pnl / costBasis) * 100;
    return { ...pos, currentPrice, marketValue, pnl, pnlPct };
  });

  const columns = [
    { title: 'Symbol', dataIndex: 'symbol', key: 'symbol', render: (s: string) => <strong>{s}</strong> },
    { title: 'Shares', dataIndex: 'shares', key: 'shares', align: 'right' as const },
    { title: 'Avg Cost', dataIndex: 'avgCost', key: 'avgCost', align: 'right' as const,
      render: (v: number) => `$${v.toFixed(2)}` },
    { title: 'Current', dataIndex: 'currentPrice', key: 'currentPrice', align: 'right' as const,
      render: (v: number) => `$${v.toFixed(2)}` },
    { title: 'Market Value', dataIndex: 'marketValue', key: 'marketValue', align: 'right' as const,
      render: (v: number) => `$${v.toLocaleString('en', { minimumFractionDigits: 2 })}` },
    { title: 'P&L', dataIndex: 'pnl', key: 'pnl', align: 'right' as const,
      render: (v: number, row: { pnlPct: number }) => (
        <span style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {v >= 0 ? '+' : ''}{v.toFixed(2)} ({row.pnlPct.toFixed(2)}%)
        </span>
      ) },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Portfolio</Title>
        <Popconfirm
          title="Reset portfolio?"
          description={`This will restore your $${STARTING_CASH.toLocaleString()} starting cash and clear all trades.`}
          onConfirm={reset} okText="Reset" cancelText="Cancel" okButtonProps={{ danger: true }}
        >
          <Button icon={<ReloadOutlined />} danger>Reset Portfolio</Button>
        </Popconfirm>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={6}>
          <Card><Statistic title="Total Value" value={totalValue} precision={2} prefix="$" /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card>
            <Statistic
              title="Total P&L"
              value={Math.abs(totalPnl)}
              precision={2}
              prefix={totalPnl >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
              valueStyle={{ color: totalPnl >= 0 ? '#52c41a' : '#ff4d4f' }}
              suffix={<Text style={{ fontSize: 13, color: totalPnl >= 0 ? '#52c41a' : '#ff4d4f' }}>
                ({totalPnlPct >= 0 ? '+' : ''}{totalPnlPct.toFixed(2)}%)
              </Text>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card><Statistic title="Cash Balance" value={portfolio.cash} precision={2} prefix="$" /></Card>
        </Col>
        <Col xs={24} sm={6}>
          <Card><Statistic title="Positions" value={portfolio.positions.length} suffix="stocks" /></Card>
        </Col>
      </Row>

      <Card title="Open Positions">
        {portfolio.positions.length === 0
          ? <Text type="secondary">No open positions. Go to Market to start trading.</Text>
          : <Table
              dataSource={posRows}
              columns={columns}
              rowKey="symbol"
              pagination={false}
              size="small"
            />
        }
      </Card>

      <Card title="Holdings by Sector" style={{ marginTop: 16 }}>
        {portfolio.positions.length === 0
          ? <Text type="secondary">No holdings yet.</Text>
          : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {posRows.map(p => (
                <Tag key={p.symbol} color={p.pnl >= 0 ? 'green' : 'red'} style={{ fontSize: 13, padding: '4px 10px' }}>
                  {p.symbol} · {p.shares} shares · ${p.marketValue.toFixed(0)}
                </Tag>
              ))}
            </div>
          )
        }
      </Card>
    </div>
  );
}
